import { v4 } from 'uuid';
import { APP, API_V1, API_V2 } from '@front-app/services/api';
import {
	FullUser,
	FullUserResponse,
	UpdateUserRequest,
	UserDetailRoleNoNone,
	UserProfileOptions,
} from '@front-app/services/UserApi/UserModel';
import { imageFetcher } from '@front-app/util/image';
import { parseFullUserResponse, ROLE_TO_API_ROLE } from '../parse';

const GUID_STORAGE_KEY = 'TR_GUID';

export function requestAppToken(): Promise<TokenResponse> {
	return APP.get('get_api_key_for_logged_in_user.shtml')
		.then(({ data }) => data)
		.catch((x) => x)
		.then(parseAppToken);
}

export function fetchCurrentUser(): Promise<FullUser> {
	return API_V1.get('/user')
		.then(({ data }) => data as FullUserResponse)
		.then(parseFullUserResponse);
}

export function fetchUser(userId: string): Promise<FullUser> {
	return API_V1.get(`/user/${userId}`)
		.then(({ data }) => data as FullUserResponse)
		.then(parseFullUserResponse);
}

export function fetchUsers(userIds: string[]): Promise<FullUser[]> {
	const params = { userIds };
	return API_V1.get('/user/multiple', { params })
		.then(({ data }) => data as FullUserResponse[])
		.then((users) => users.map(parseFullUserResponse));
}

export function fetchColleagues(): Promise<FullUser[]> {
	return API_V1.get('/user/colleagues')
		.then(({ data }) => data as FullUserResponse[])
		.then((users) => users.map(parseFullUserResponse));
}

export function logIn(email: string, pwd: string): Promise<string> {
	const guid = getAndSetGUID();
	const route = `/login/${email}`;
	const params = {
		guid,
		pwd,
	};
	return API_V1.post(route, null, { params })
		.then(({ data }) => data as string);
}

export function logOut(): Promise<void> {
	return Promise.all([
		API_V1.delete('/session'), // Removes the API session if there is one
		APP.get('api_logout.shtml'), // Removes the TRApp tomcat session if there is one
	])
		.then(() => {
			// do nothing
		})
		.catch(() => {
			// eslint-disable-next-line no-console
			console.warn('Error logging out');
		});
}

export function requestRoleVerification(): Promise<void> {
	return API_V1.post('/user/verify').then(() => {
		// void promise
	});
}

export enum ActivationStatus {
	SUCCESS = 'success',
	ALREADY_ACTIVATED = 'alreadyActivated',
	ERROR = 'error',
}

export interface ActivationResult {
	status: ActivationStatus;
	token: string | null;
}

function getActivationStatus(code: number): ActivationStatus {
	switch (code) {
	case 200:
		return ActivationStatus.SUCCESS;
	case 209:
		return ActivationStatus.ALREADY_ACTIVATED;
	default:
		return ActivationStatus.ERROR;
	}
}

export async function activateAccount(token: string): Promise<ActivationResult> {
	const response = await API_V1.get(`/login/activate/${token}`);
	const status = getActivationStatus(response.status);
	return {
		status,
		token: status === ActivationStatus.SUCCESS ? response?.data : null,
	};
}

export async function resendActivationCode(email: string): Promise<void> {
	await API_V1.get(`/login/resend/${email}`);
}

export function getUserProfileOptions(): Promise<UserProfileOptions> {
	return API_V1.get('/user/profileOptions')
		.then(({ data }) => data as UserProfileOptions);
}

export const getUserAvatar = imageFetcher((userId: string) => `/users/${userId}/avatar`);

export function updateUserProfile(params: Partial<UpdateUserRequest>): Promise<FullUser> {
	return API_V1.put('/user/profile', params)
		.then(({ data }) => data as FullUserResponse)
		.then(parseFullUserResponse);
}

interface UpdateUserRolesRequest {
	organizationId: string;
	positionTitle: string;
	startMonth: string;
	startYear: string;
	mobile: string;
	roles: UserDetailRoleNoNone[];
}

const UpdateUserRolesRequestSerializer = (params: UpdateUserRolesRequest): string => Object.entries(params)
	.map(([key, val]) => {
		if (key === 'roles') {
			return val.map((role: UserDetailRoleNoNone) => `roles=${encodeURIComponent(role)}`).join('&');
		}
		return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
	})
	.join('&');

export function updateUserRoles(params: Partial<UpdateUserRolesRequest>): Promise<FullUser> {
	return API_V1.put('/user', undefined, {
		params: {
			...params,
			roles: (params.roles || []).map((r) => ROLE_TO_API_ROLE[r]),
		},
		paramsSerializer: UpdateUserRolesRequestSerializer,
	})
		.then(({ data }) => data as FullUserResponse)
		.then(parseFullUserResponse);
}

interface UpdateEmailRequest {
	currentPassword: string;
	newEmailAddress: string;
}

export function updateUserEmail(params: UpdateEmailRequest): Promise<void> {
	return API_V2.patch('/users/email', params);
}

interface UpdatePasswordRequest {
	currentPassword: string;
	newPassword: string;
}

export function updateUserPassword(params: UpdatePasswordRequest): Promise<void> {
	return API_V2.patch('/users/password', params);
}

type UploadHandler = (e: ProgressEvent) => void;

// eslint-disable-next-line @typescript-eslint/no-empty-function
export function updateUserAvatar(avatar: File, onUploadProgress: UploadHandler = () => {}): Promise<void> {
	const innerRequest = new FormData();
	innerRequest.append('file', avatar);
	return API_V1.post('/avatars', innerRequest, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
		onUploadProgress,
	});
}

interface TokenResponse {
	apiKey: string | null;
	failureReason: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseAppToken(data: any): TokenResponse {
	try {
		if (data.apiKey || data.failureReason) {
			return data;
		}
		return JSON.parse(data);
	} catch (e) {
		return { failureReason: 'user not logged in', apiKey: null };
	}
}

function getAndSetGUID() {
	const existing = window.localStorage.getItem(GUID_STORAGE_KEY);
	if (existing) {
		return existing;
	}
	const newGuid = v4();
	window.localStorage.setItem(GUID_STORAGE_KEY, newGuid);
	return newGuid;
}
